import type { Language } from "../config/site";

export interface SupportItem {
  question: string;
  answer: string[];
}

export const supportContent = {
  en: {
    eyebrow: "SUPPORT",
    title: "How can we help?",
    introduction: "VaultPin is designed to stay simple. These answers cover the most common questions about the Daily Vault, purchases, Game Center and device settings.",
    contactTitle: "Still need help?",
    contactBody: "Email support with the app version, your iPhone model, iOS version and a clear description of what happened. Do not send passwords, access tokens, Game Center signatures or other sensitive data.",
    contactAction: "Email support@vaultpin.app",
    items: [
      {
        question: "When does the Daily Vault refresh?",
        answer: ["A new Daily Vault cycle starts worldwide at 16:00 UTC. The optional New Vault local notification is scheduled for 16:05 UTC."],
      },
      {
        question: "How do I turn reminders on or off?",
        answer: ["Open Settings in VaultPin and use Daily Vault Reminder. iOS may ask for notification permission the first time. You can also manage VaultPin notification permission in the iPhone Settings app."],
      },
      {
        question: "How do I turn game sounds off?",
        answer: ["Open VaultPin Settings and turn Sound Effects off. This preference remains on your device."],
      },
      {
        question: "How do I restore my purchase?",
        answer: ["Open VaultPin Settings and choose Restore Purchases, or use the same option on the practice-mode purchase sheet. Apple handles restoration for the Apple account that made the purchase."],
      },
      {
        question: "Does deleting my data remove my purchase?",
        answer: ["No. Deleting VaultPin gameplay data does not remove the StoreKit entitlement managed by Apple. Medium, Hard and Expert can remain unlocked or be restored through Apple."],
      },
      {
        question: "How do I delete my VaultPin data?",
        answer: ["Open Settings → Delete VaultPin Data, read the confirmation and choose Delete Data. Daily Vault history, leaderboard results and the VaultPin player identity are deleted after the authenticated request succeeds. The action cannot be undone."],
      },
      {
        question: "Why is my Game Center name on the leaderboard?",
        answer: ["When Game Center verification succeeds and a winning session is eligible, the verified Game Center alias may appear with rank, attempts, time and hint use. Guest sessions can view the leaderboard but are not ranked."],
      },
      {
        question: "Why does the Daily Vault need an internet connection?",
        answer: ["The Daily Vault is shared and backend-authoritative, so it needs a connection to start, submit guesses, request a hint, recover progress and load rankings. Practice modes are local and can be played offline."],
      },
      {
        question: "What should I do after a connection error?",
        answer: ["Check your connection and try again. Do not delete the app or reset Keychain data as a first step; VaultPin may be able to recover the same Daily Vault session automatically."],
      },
    ],
  },
  tr: {
    eyebrow: "DESTEK",
    title: "Nasıl yardımcı olabiliriz?",
    introduction: "VaultPin sade kalacak şekilde tasarlandı. Bu yanıtlar Günlük Kasa, satın alımlar, Game Center ve cihaz ayarları hakkındaki en yaygın soruları kapsar.",
    contactTitle: "Yine de yardıma mı ihtiyacın var?",
    contactBody: "Uygulama sürümü, iPhone modelin, iOS sürümün ve yaşananların açık bir açıklamasıyla destek ekibine e-posta gönder. Parola, access token, Game Center imzası veya başka hassas veri gönderme.",
    contactAction: "support@vaultpin.app adresine yaz",
    items: [
      {
        question: "Günlük Kasa ne zaman yenilenir?",
        answer: ["Yeni Günlük Kasa cycle'ı dünya genelinde 16:00 UTC'de başlar. İsteğe bağlı Yeni Kasa yerel bildirimi 16:05 UTC için planlanır."],
      },
      {
        question: "Hatırlatıcıyı nasıl açıp kapatırım?",
        answer: ["VaultPin'de Ayarlar'ı aç ve Günlük Kasa Hatırlatıcısı'nı kullan. iOS ilk kez bildirim izni isteyebilir. İzni iPhone Ayarlar uygulamasından da yönetebilirsin."],
      },
      {
        question: "Oyun seslerini nasıl kapatırım?",
        answer: ["VaultPin Ayarlar ekranında Ses Efektleri'ni kapat. Bu tercih cihazında kalır."],
      },
      {
        question: "Satın alımımı nasıl geri yüklerim?",
        answer: ["VaultPin Ayarlar ekranında Satın Alımları Geri Yükle'yi seç veya pratik modu satın alma ekranındaki aynı seçeneği kullan. Satın alımı yapan Apple hesabı için geri yüklemeyi Apple yönetir."],
      },
      {
        question: "Verilerimi silmek satın alımımı kaldırır mı?",
        answer: ["Hayır. VaultPin oyun verilerini silmek Apple tarafından yönetilen StoreKit entitlement'ını kaldırmaz. Orta, Zor ve Uzman açık kalabilir veya Apple üzerinden geri yüklenebilir."],
      },
      {
        question: "VaultPin verilerimi nasıl silerim?",
        answer: ["Ayarlar → VaultPin Verilerini Sil yolunu aç, onayı oku ve Verileri Sil'i seç. Günlük Kasa geçmişi, sıralama sonuçları ve VaultPin oyuncu kimliği, kimliği doğrulanmış istek başarıyla tamamlandıktan sonra silinir. İşlem geri alınamaz."],
      },
      {
        question: "Game Center adım neden sıralamada görünüyor?",
        answer: ["Game Center doğrulaması başarılı olduğunda ve kazanan session sıralamaya uygunsa doğrulanmış Game Center alias'ın sıra, tahmin, süre ve ipucu kullanımıyla birlikte görünebilir. Misafir session'lar sıralamayı görebilir ancak sıralanmaz."],
      },
      {
        question: "Günlük Kasa neden internet bağlantısı istiyor?",
        answer: ["Günlük Kasa paylaşılan ve backend-authoritative olduğu için başlatma, tahmin gönderme, ipucu isteme, ilerlemeyi geri yükleme ve sıralamayı yükleme sırasında bağlantı gerekir. Pratik modları yereldir ve çevrimdışı oynanabilir."],
      },
      {
        question: "Bağlantı hatasından sonra ne yapmalıyım?",
        answer: ["Bağlantını kontrol et ve yeniden dene. İlk adım olarak uygulamayı silme veya Keychain verisini sıfırlama; VaultPin aynı Günlük Kasa session'ını otomatik olarak geri yükleyebilir."],
      },
    ],
  },
} satisfies Record<Language, {
  eyebrow: string;
  title: string;
  introduction: string;
  contactTitle: string;
  contactBody: string;
  contactAction: string;
  items: SupportItem[];
}>;
